import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Alert, Box, Typography, type SxProps, type Theme } from '@mui/material';
import { ReactNode } from 'react';

import useTranslate from 'hooks/useTranslate';

type StepNoteVariant = 'info' | 'tip' | 'warning';

interface StepNoteProps {
    /** i18n key — preferred over raw children for consistency. */
    messageKey?: string;
    /** Optional values interpolated into the translation (simple suffix text). */
    messageSuffix?: string;
    /** Raw content when a key is not enough (e.g. multi-line). */
    children?: ReactNode;
    variant?: StepNoteVariant;
    sx?: SxProps<Theme>;
    /** When true, note spans full grid width inside FormGridLayout. */
    fullWidth?: boolean;
}

const severityMap: Record<StepNoteVariant, 'info' | 'success' | 'warning'> = {
    info: 'info',
    tip: 'info',
    warning: 'warning',
};

/**
 * Modern, mobile-friendly callout for form step guidance notes
 * (mirrors AssessmentV2 SubTitle / AssessmentQuestion notes).
 */
const StepNote = ({
    messageKey,
    messageSuffix,
    children,
    variant = 'info',
    sx,
    fullWidth = false,
}: StepNoteProps) => {
    const { translate } = useTranslate();

    const body =
        children ??
        (messageKey ? (
            <>
                {translate(messageKey)}
                {messageSuffix ? ` ${messageSuffix}` : null}
            </>
        ) : null);

    if (!body) return null;

    return (
        <Box
            sx={{
                width: '100%',
                mb: { xs: 2, sm: 2.5 },
                ...(fullWidth ? { gridColumn: '1 / -1' } : {}),
                ...((sx as object) || {}),
            }}
        >
            <Alert
                severity={severityMap[variant]}
                icon={<InfoOutlinedIcon fontSize="small" />}
                sx={{
                    alignItems: 'flex-start',
                    borderRadius: 2,
                    py: { xs: 1, sm: 1.25 },
                    px: { xs: 1.25, sm: 1.75 },
                    bgcolor: (theme) =>
                        variant === 'warning'
                            ? theme.palette.mode === 'dark'
                                ? 'rgba(237, 108, 2, 0.12)'
                                : 'rgba(237, 108, 2, 0.08)'
                            : theme.palette.mode === 'dark'
                              ? 'rgba(96, 37, 216, 0.16)'
                              : 'rgba(96, 37, 216, 0.06)',
                    color: 'text.primary',
                    border: '1px solid',
                    borderColor: (theme) =>
                        variant === 'warning'
                            ? 'warning.light'
                            : theme.palette.mode === 'dark'
                              ? 'rgba(96, 37, 216, 0.35)'
                              : 'rgba(96, 37, 216, 0.18)',
                    '& .MuiAlert-icon': {
                        color: variant === 'warning' ? 'warning.main' : 'primary.main',
                        mt: 0,
                        mr: 1,
                        p: 0.25,
                        opacity: 0.9,
                    },
                    '& .MuiAlert-message': {
                        width: '100%',
                        py: 0.25,
                    },
                }}
            >
                <Typography
                    component="div"
                    variant="body2"
                    sx={{
                        fontSize: { xs: '0.8125rem', sm: '0.875rem' },
                        lineHeight: 1.55,
                        fontWeight: 500,
                        color: 'text.secondary',
                        '& strong, & b': {
                            fontWeight: 700,
                            color: 'text.primary',
                        },
                    }}
                >
                    {body}
                </Typography>
            </Alert>
        </Box>
    );
};

export default StepNote;
